/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Quick Schedule form utilities
 *
 * @file quickScheduleFormUtils.js
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import { TIMEFIELD_CONSTANTS } from '../../common/constants';
import { matchTimezoneToOptions, parseDate } from '../../common/dateTimeUtils';
import { buildQuickScheduleAssignments } from './quickScheduleReviewUtils';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_OUT_OF_RANGE_ERROR = 'Choose a date within your course schedule';

export const EMPTY_QUICK_SCHEDULE_FORM_VALUES = {
  dueDays: [],
  dueTime: '',
  timeZone: '',
  startDate: '',
  endDate: '',
  autoNameAssignments: true
};

function toLocalCalendarDate(date, dayOffset = 0) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + dayOffset);
}

function getLocalDayMs(date) {
  return toLocalCalendarDate(date).getTime();
}

function getLocalCalendarDayDiff(laterDate, earlierDate) {
  return Math.round((getLocalDayMs(laterDate) - getLocalDayMs(earlierDate)) / MS_PER_DAY);
}

export function resolveDefaultDueTime(dueTime) {
  if (dueTime) {
    return dueTime;
  }

  return TIMEFIELD_CONSTANTS.DEFAULT_FORM_TIME;
}

export function resolveDefaultTimeZone(timeZone, timeZoneOptions = []) {
  // Keep an explicit value from parent/assignment as-is.
  if (timeZone) {
    return timeZone;
  }

  // No explicit value: match the browser timezone, or leave empty if no match.
  return matchTimezoneToOptions(timeZoneOptions) || '';
}

/**
 * Start date ISO for the picked calendar day, preserving local time-of-day
 * from the existing value (or now when unset).
 */
export function toStartDateIso(pickedDate, referenceIso = '') {
  if (!pickedDate || Number.isNaN(pickedDate.getTime())) {
    return '';
  }

  const reference = referenceIso ? new Date(referenceIso) : new Date();

  if (referenceIso && Number.isNaN(reference.getTime())) {
    return '';
  }

  return new Date(
    pickedDate.getFullYear(),
    pickedDate.getMonth(),
    pickedDate.getDate(),
    reference.getHours(),
    reference.getMinutes(),
    reference.getSeconds(),
    reference.getMilliseconds()
  ).toISOString();
}

/**
 * End date ISO as start + whole 24h periods for each local calendar day after start.
 */
export function toEndDateIso(pickedEndDate, startDateIso) {
  if (!pickedEndDate || Number.isNaN(pickedEndDate.getTime()) || !startDateIso) {
    return '';
  }

  const start = new Date(startDateIso);

  if (Number.isNaN(start.getTime())) {
    return '';
  }

  const dayDiff = getLocalCalendarDayDiff(pickedEndDate, start);

  if (dayDiff < 1) {
    return '';
  }

  return new Date(start.getTime() + dayDiff * MS_PER_DAY).toISOString();
}

/** Local calendar Date for the picker from a stored ISO string. */
export function parseDateForPicker(isoString) {
  if (!isoString) {
    return null;
  }

  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return toLocalCalendarDate(date);
}

/** Earliest selectable end date: local calendar day after start, or tomorrow. */
export function getEndDateMinDate(startDateValue) {
  if (startDateValue) {
    const start = parseDateForPicker(startDateValue);

    if (start) {
      return toLocalCalendarDate(start, 1);
    }
  }

  return toLocalCalendarDate(new Date(), 1);
}

/** Converts a stored ISO date to a local calendar Date for picker comparisons. */
function toLocalCalendarDateFromIso(isoValue) {
  if (!isoValue) {
    return null;
  }

  const dateOnly = String(isoValue).includes('T') ? String(isoValue).split('T')[0] : isoValue;
  const date = parseDate(dateOnly);

  if (!date) {
    return null;
  }

  return toLocalCalendarDate(date);
}

/** True when the stored ISO date falls on a local calendar day before `minDate`. */
function isDayBefore(isoValue, minDate) {
  const date = parseDateForPicker(isoValue);

  return Boolean(date && minDate) && getLocalDayMs(date) < getLocalDayMs(minDate);
}

/** True when the stored ISO date falls on a local calendar day after `maxDate`. */
function isDayAfter(isoValue, maxDate) {
  const date = parseDateForPicker(isoValue);

  return Boolean(date && maxDate) && getLocalDayMs(date) > getLocalDayMs(maxDate);
}

/** Earliest selectable start date: the course start day, but never before today. */
export function getCourseBoundedStartMinDate(courseDateBounds = {}) {
  const today = toLocalCalendarDate(new Date());
  const courseStart = toLocalCalendarDateFromIso(courseDateBounds.courseStartDate);

  return courseStart && courseStart > today ? courseStart : today;
}

/** Latest selectable end date: the course end day. */
export function getCourseBoundedEndMaxDate(courseDateBounds = {}) {
  return toLocalCalendarDateFromIso(courseDateBounds.courseEndDate);
}

/** Latest selectable start date: the day before course end, so end date (start + 1) still fits. */
export function getCourseBoundedStartMaxDate(courseDateBounds = {}) {
  const courseEnd = getCourseBoundedEndMaxDate(courseDateBounds);

  return courseEnd ? toLocalCalendarDate(courseEnd, -1) : null;
}

/** True when the start date is after the course-bounded maximum. */
export function isStartDateAfterCourseMax(startDateIso, courseDateBounds = {}) {
  return isDayAfter(startDateIso, getCourseBoundedStartMaxDate(courseDateBounds));
}

/** True when the end date is after the course end date. */
export function isEndDateAfterCourseMax(endDateIso, courseDateBounds = {}) {
  return isDayAfter(endDateIso, getCourseBoundedEndMaxDate(courseDateBounds));
}

/** react-hook-form validate rule: start date must be on or after course start (and not in the past). */
export function buildStartDateInRangeValidator(courseDateBounds = {}, errorMessage = '') {
  const { courseStartDate, courseEndDate } = courseDateBounds;

  return (value) => {
    if (!value || (!courseStartDate && !courseEndDate)) {
      return true;
    }

    const isOutOfRange = isDayBefore(value, getCourseBoundedStartMinDate(courseDateBounds))
      || isStartDateAfterCourseMax(value, courseDateBounds);

    return isOutOfRange ? (errorMessage || DEFAULT_OUT_OF_RANGE_ERROR) : true;
  };
}

/** react-hook-form validate rule: end date must be on or before course end. */
export function buildEndDateInRangeValidator(courseDateBounds = {}, errorMessage = '') {
  return (value) => (
    isEndDateAfterCourseMax(value, courseDateBounds) ? (errorMessage || DEFAULT_OUT_OF_RANGE_ERROR) : true
  );
}

/**
 * Default start date: an explicit value from parent/assignment as-is, otherwise the
 * course-bounded minimum (course start day, or today if the course already started).
 */
export function resolveDefaultStartDate(startDate, courseDateBounds = {}) {
  if (startDate) {
    return startDate;
  }

  return toStartDateIso(getCourseBoundedStartMinDate(courseDateBounds));
}

export function buildFormDefaultValues(initialValues = {}, timeZoneOptions = [], courseDateBounds = {}) {
  const {
    dueDays,
    dueTime,
    timeZone,
    startDate,
    endDate,
    autoNameAssignments
  } = initialValues;

  return {
    ...EMPTY_QUICK_SCHEDULE_FORM_VALUES,
    dueDays: dueDays ?? EMPTY_QUICK_SCHEDULE_FORM_VALUES.dueDays,
    dueTime: resolveDefaultDueTime(dueTime),
    timeZone: resolveDefaultTimeZone(timeZone, timeZoneOptions),
    startDate: resolveDefaultStartDate(startDate, courseDateBounds),
    endDate: endDate ?? EMPTY_QUICK_SCHEDULE_FORM_VALUES.endDate,
    autoNameAssignments: autoNameAssignments ?? EMPTY_QUICK_SCHEDULE_FORM_VALUES.autoNameAssignments
  };
}

/** Whether a stored end date is before the local calendar day after start. */
export function isEndDateBeforeMinimum(endDateIso, startDateIso) {
  if (!endDateIso || !startDateIso) {
    return false;
  }

  const end = parseDateForPicker(endDateIso);
  const start = parseDateForPicker(startDateIso);

  if (!end || !start) {
    return false;
  }

  return getLocalCalendarDayDiff(end, start) < 1;
}

export function hasMatchingDueDaysInDateRange(formValues = {}) {
  const {
    dueDays = [],
    startDate = '',
    endDate = ''
  } = formValues;

  if (!dueDays.length || !startDate || !endDate) {
    return true;
  }

  return buildQuickScheduleAssignments(formValues).length > 0;
}

export const isSubmitButtonDisabled = ({
  isSubmitting,
  isValid,
  hasValidDueDayRange = true
}) => (
  isSubmitting || !isValid || !hasValidDueDayRange
);
